import { createFileRoute, Link } from "@tanstack/react-router";
import heroCotto from "@/assets/hero-cotto.webp";
import prima1 from "@/assets/prima1.webp.asset.json";
import dopo1 from "@/assets/dopo1.webp.asset.json";
import prima2 from "@/assets/prima2.webp.asset.json";
import dopo2 from "@/assets/dopo2.webp.asset.json";
import terrazzaCotto from "@/assets/pavimento-cotto-terrazza-lavato-brescia.webp.asset.json";
import porticoCotto from "@/assets/portico-cotto-esterno-trattato-lombardia.webp.asset.json";
import scalaCotto from "@/assets/scala-in-cotto-antico-pulita.webp.asset.json";
import { BeforeAfter } from "@/components/BeforeAfter";
import { PreventivoForm } from "@/components/PreventivoForm";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SocialLinks } from "@/components/Social";
import {
  ANNI_ESPERIENZA,
  ANNO_FONDAZIONE,
  EMAIL,
  TEL_ANTONIO,
  TEL_LUCA,
  WHATSAPP_URL,
  province,
} from "@/lib/site";

const URL_PAGINA = "https://artigianidelcotto.com/pulizia-lavaggio-cotto";
const TITOLO = "Pulizia e lavaggio del cotto in Lombardia | Artigiani";
const DESCRIZIONE =
  "Lavaggio professionale e deceratura del cotto in casa, ufficio e negozio. Interveniamo in tutta la Lombardia: sopralluogo gratuito, preventivo senza impegno. Chiama 339 751 7813.";

const servizi = [
  {
    titolo: "Lavaggio profondo del cotto",
    testo:
      "Rimuoviamo lo sporco di anni, gli aloni di detersivo e la patina grigia che rende opaco il pavimento. Usiamo detergenti acido tamponati a base d'acqua, sicuri per il laterizio e per chi abita la casa.",
    voci: ["Cotto antico e moderno", "Pavimenti in cotto cucina e soggiorno", "Scale e corridoi", "Recupero dopo lavori edili"],
  },
  {
    titolo: "Deceratura e rigenerazione",
    testo:
      "Quando la cera vecchia si è ingiallita o screpolata, la sciogliamo e la eliminiamo senza aggredire il cotto. Poi applichiamo un nuovo fondo antimacchia e la finitura concordata: il pavimento torna vivo.",
    voci: ["Rimozione cere ingiallite", "Fondo antimacchia", "Cera lucida o satinata", "Tonalizzazione del colore"],
  },
  {
    titolo: "Trattamento protettivo antimacchia",
    testo:
      "Dopo il lavaggio proteggiamo la superficie con prodotti specifici per il cotto: l'acqua e il vino non penetrano, le macchie si rimuovono facilmente e il pavimento resta più a lungo pulito.",
    voci: ["Idrorepellente traspirante", "Antimacchia per cotto", "Finitura naturale o lucida", "Manutenzione periodica"],
  },
];

const passaggi = [
  {
    titolo: "Sopralluogo e preventivo",
    testo: "Valutiamo lo stato del cotto, lo spessore della cera e le macchie. Ricevi un preventivo scritto, chiaro e senza impegno.",
  },
  {
    titolo: "Lavaggio e deceratura",
    testo: "Detergiamo con prodotti professionali e, se serve, rimuoviamo i vecchi strati di cera per preparare il pavimento al nuovo trattamento.",
  },
  {
    titolo: "Asciugatura",
    testo: "Il cotto deve essere perfettamente asciutto prima della protezione: rispettiamo i tempi del materiale, non quelli dell'orologio.",
  },
  {
    titolo: "Protezione e finitura",
    testo: "Applichiamo il fondo antimacchia e la finitura scelta: lucida, satinata o effetto naturale, secondo l'uso e il tuo gusto.",
  },
];

const lavori = [
  { label: "Cortile in cotto lavato e recuperato", before: prima1.url, after: dopo1.url },
  { label: "Terrazza in cotto decerata e protetta", before: prima2.url, after: dopo2.url },
];

const galleria = [
  {
    src: terrazzaCotto.url,
    alt: "Pavimento in cotto di una terrazza dopo lavaggio e trattamento protettivo in provincia di Brescia",
    w: 1120,
    h: 1400,
  },
  {
    src: porticoCotto.url,
    alt: "Portico con pavimento in cotto a spina di pesce lavato e trattato in Lombardia",
    w: 1050,
    h: 1400,
  },
  {
    src: scalaCotto.url,
    alt: "Scala in cotto antico fatto a mano dopo pulizia professionale e deceratura",
    w: 1050,
    h: 1400,
  },
];

const faq = [
  {
    q: "Quanto costa lavare e trattare un pavimento in cotto?",
    a: "Il prezzo dipende dai metri quadri e dallo stato del pavimento: un cotto con cera vecchia e macchie richiede più lavoro di uno semplicemente sporco. Per una stima precisa facciamo un sopralluogo gratuito in tutta la Lombardia.",
  },
  {
    q: "Quanto dura il trattamento dopo il lavaggio?",
    a: "In una casa abitata normalmente, la finitura dura alcuni anni. Con una manutenzione periodica e il rinnovo della protezione il pavimento resta bello a lungo, senza bisogno di rifarlo.",
  },
  {
    q: "Si può abitare in casa durante il lavaggio del cotto?",
    a: "Sì, nella maggior parte dei casi. Lavoriamo per zone, proteggiamo arredi e battiscopa e lasciamo il cantiere pulito. Le stanze trattate restano libere solo per il tempo di asciugatura.",
  },
  {
    q: "Lavate anche cotto esterno, cortili e terrazze?",
    a: "Sì. Per l'esterno usiamo protettivi traspiranti diversi da quelli interni, studiati per resistere a pioggia, gelo e passaggio. Puoi vedere i dettagli nella pagina dedicata al trattamento cotto esterno.",
  },
  {
    q: "Come richiedere un preventivo?",
    a: "Basta una telefonata o un messaggio WhatsApp al 339 751 7813. Anche due foto e i metri quadri indicativi bastano per una prima stima. Il sopralluogo e il preventivo sono gratuiti.",
  },
];

export const Route = createFileRoute("/pulizia-lavaggio-cotto")({
  component: PuliziaLavaggioCotto,
  head: () => ({
    meta: [
      { title: TITOLO },
      { name: "description", content: DESCRIZIONE },
      { property: "og:title", content: TITOLO },
      { property: "og:description", content: DESCRIZIONE },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_PAGINA },
      { property: "og:image", content: `https://artigianidelcotto.com${dopo1.url}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://artigianidelcotto.com${dopo1.url}` },
      { name: "geo.region", content: "IT-25" },
    ],
    links: [{ rel: "canonical", href: URL_PAGINA }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "HomeAndConstructionBusiness",
              "@id": "https://artigianidelcotto.com/#azienda",
              name: "Artigiani del Cotto",
              url: "https://artigianidelcotto.com/",
              telephone: "+39 339 751 7813",
              email: EMAIL,
              image: `https://artigianidelcotto.com${dopo1.url}`,
              foundingDate: String(ANNO_FONDAZIONE),
              priceRange: "€€",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Torbole Casaglia",
                addressRegion: "BS",
                addressCountry: "IT",
              },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Lombardia" },
                ...province.map((p) => ({ "@type": "City", name: p })),
              ],
            },
            {
              "@type": "WebPage",
              "@id": `${URL_PAGINA}#pagina`,
              url: URL_PAGINA,
              name: TITOLO,
              description: DESCRIZIONE,
              inLanguage: "it-IT",
              primaryImageOfPage: { "@type": "ImageObject", url: `https://artigianidelcotto.com${dopo1.url}` },
              isPartOf: { "@type": "WebSite", "@id": "https://artigianidelcotto.com/#sito", url: "https://artigianidelcotto.com/", name: "Artigiani del Cotto" },
              breadcrumb: { "@id": `${URL_PAGINA}#briciole` },
            },
            {
              "@type": "Service",
              "@id": `${URL_PAGINA}#servizio`,
              name: "Pulizia e lavaggio del cotto",
              serviceType: "Lavaggio, deceratura e trattamento protettivo di pavimenti in cotto",
              category: "Manutenzione e trattamento pavimenti",
              description: DESCRIZIONE,
              url: URL_PAGINA,
              image: [
                `https://artigianidelcotto.com${dopo1.url}`,
                `https://artigianidelcotto.com${dopo2.url}`,
              ],
              provider: { "@id": "https://artigianidelcotto.com/#azienda" },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Lombardia" },
                ...province.map((p) => ({ "@type": "City", name: p })),
              ],
              mainEntityOfPage: { "@id": `${URL_PAGINA}#pagina` },
              offers: {
                "@type": "Offer",
                priceCurrency: "EUR",
                availability: "https://schema.org/InStock",
                url: `${URL_PAGINA}#contatti`,
                description: "Sopralluogo e preventivo gratuiti in tutta la Lombardia.",
              },
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Servizi di pulizia e lavaggio del cotto",
                itemListElement: servizi.map((s) => ({
                  "@type": "Offer",
                  itemOffered: { "@type": "Service", name: s.titolo, description: s.testo },
                })),
              },
            },
            {
              "@type": "FAQPage",
              "@id": `${URL_PAGINA}#faq`,
              mainEntityOfPage: { "@id": `${URL_PAGINA}#pagina` },
              inLanguage: "it-IT",
              mainEntity: faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${URL_PAGINA}#briciole`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://artigianidelcotto.com/" },
                { "@type": "ListItem", position: 2, name: "Pulizia e lavaggio del cotto", item: URL_PAGINA },
              ],
            },
          ],
        }),
      },
    ],
  }),
});

function PuliziaLavaggioCotto() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main>
        <section className="gradient-warm py-16 text-background sm:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <nav aria-label="Percorso" className="text-sm text-background/70">
              <Link to="/" className="underline-offset-4 hover:underline">Home</Link>
              <span className="px-2">/</span>
              <span>Pulizia e lavaggio del cotto</span>
            </nav>
            <p className="eyebrow mt-6 text-sand">Dal {ANNO_FONDAZIONE} in tutta la Lombardia</p>
            <h1 className="mt-4 max-w-3xl text-4xl leading-[1.08] font-semibold sm:text-5xl">
              Pulizia e lavaggio del cotto in Lombardia
            </h1>
            <p className="mt-5 max-w-2xl text-base text-background/90 sm:text-lg">
              Recuperiamo pavimenti in cotto opachi, macchiati o pieni di cera vecchia. Lavaggio professionale,
              deceratura e trattamento protettivo in casa, ufficio e negozio. {ANNI_ESPERIENZA} di esperienza,
              sopralluogo gratuito e preventivo senza impegno.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#contatti"
                className="rounded-xs bg-copper px-6 py-3 text-sm font-semibold text-accent-foreground transition-transform hover:-translate-y-0.5"
              >
                Richiedi un preventivo gratuito
              </a>
              <a
                href={`tel:${TEL_ANTONIO}`}
                className="rounded-xs border border-background/40 px-6 py-3 text-sm font-semibold transition-colors hover:bg-background/10"
              >
                Chiama Antonio: 339 751 7813
              </a>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <p className="eyebrow">Perché lavare il cotto</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                Il cotto si sporca in profondità: noi lo puliamo davvero.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Il cotto è poroso: assorbe lo sporco, gli aloni di detersivo, il vino, l'olio e le cere vecchie.
                Con il tempo diventa opaco, ingiallito e difficile da pulire con i normali prodotti domestici.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Il nostro lavaggio professionale rimuove lo sporco dalla profondità della mattonella, senza rovinarne
                la superficie. Dopo, applichiamo un fondo antimacchia e la finitura scelta: il pavimento torna
                caldo, vivo e facile da mantenere.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Pulizia profonda con detergenti specifici per cotto",
                  "Rimozione cere ingiallite e trattamenti sfogliati",
                  "Fondo antimacchia e finitura lucida o satinata",
                  "Interventi in abitazioni, uffici, negozi e attività",
                ].map((t) => (
                  <li key={t} className="flex gap-3 text-sm">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-copper" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="self-start">
              <img
                src={heroCotto}
                alt="Pavimento in cotto dopo lavaggio e trattamento"
                width={1200}
                height={800}
                loading="eager"
                decoding="async"
                className="w-full rounded-sm object-cover shadow-[var(--shadow-soft)]"
              />
            </div>
          </div>
        </section>

        <section id="servizi" className="scroll-mt-20 bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow">I nostri servizi</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Cosa facciamo sul tuo pavimento in cotto.
            </h2>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {servizi.map((s, i) => (
                <article
                  key={s.titolo}
                  className="surface-warm flex flex-col rounded-sm border border-border p-7 transition-transform hover:-translate-y-1"
                >
                  <span className="font-display text-sm font-semibold text-copper">0{i + 1}</span>
                  <h3 className="mt-3 text-xl font-semibold">{s.titolo}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.testo}</p>
                  <ul className="mt-6 space-y-2 border-t border-border pt-5 text-sm">
                    {s.voci.map((v) => (
                      <li key={v} className="flex gap-2">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-copper" />
                        <span>{v}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <p className="eyebrow">Il metodo</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Quattro passaggi per un cotto come nuovo.
          </h2>
          <ol className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {passaggi.map((p, i) => (
              <li key={p.titolo} className="border-t-2 border-copper pt-5">
                <span className="font-display text-sm font-semibold text-copper">Fase {i + 1}</span>
                <h3 className="mt-2 text-lg font-semibold">{p.titolo}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.testo}</p>
              </li>
            ))}
          </ol>
        </section>

        <section id="lavori" className="scroll-mt-20 bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow">Prima e dopo</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Risultati visibili: trascina il cursore.
            </h2>
            <div className="mt-12 grid gap-8 sm:grid-cols-2">
              {lavori.map((l) => (
                <BeforeAfter key={l.after} {...l} />
              ))}
            </div>

            <p className="eyebrow mt-16">Altri lavori</p>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {galleria.map((g) => (
                <img
                  key={g.alt}
                  src={g.src}
                  alt={g.alt}
                  width={g.w}
                  height={g.h}
                  loading="lazy"
                  decoding="async"
                  className="aspect-4/3 w-full rounded-sm object-cover shadow-[var(--shadow-soft)]"
                />
              ))}
            </div>
          </div>
        </section>

        <section id="trattamento-pavimenti-cotto" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20">
          <p className="eyebrow">Trattamento</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Trattamento per pavimenti in cotto: quale scegliere.
          </h2>
          <div className="mt-8 grid gap-8 lg:grid-cols-3">
            <div className="surface-warm rounded-sm border border-border p-7">
              <h3 className="text-lg font-semibold">Cera per interni</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                È il trattamento tradizionale del cotto di casa: dà calore al colore e una finitura
                lucida o satinata. Va rinnovata ogni tanto, ma si rigenera facilmente senza rifare
                tutto il pavimento.
              </p>
            </div>
            <div className="surface-warm rounded-sm border border-border p-7">
              <h3 className="text-lg font-semibold">Protettivo idro-oleorepellente</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Penetra nel laterizio e ferma acqua, vino e olio senza formare pellicola. È la scelta
                giusta per cucine, ingressi e ambienti molto usati, e per chi vuole un effetto naturale.
              </p>
            </div>
            <div className="surface-warm rounded-sm border border-border p-7">
              <h3 className="text-lg font-semibold">Protettivo traspirante per esterno</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Su terrazze, portici e cortili serve un prodotto che resista a pioggia e gelo e lasci
                evaporare l'umidità: la cera all'aperto si sfoglia in poche stagioni.
              </p>
            </div>
          </div>
          <p className="mt-8 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Scegliamo il trattamento dopo il lavaggio, in base al materiale e a come vivi la casa. Per
            l'esterno vedi{" "}
            <Link to="/trattamento-cotto-esterno" className="text-primary underline underline-offset-4">
              trattamento cotto esterno
            </Link>
            ; se hai anche{" "}
            <Link to="/trattamento-marmo-pietre-graniglia" className="text-primary underline underline-offset-4">
              marmo, pietre o graniglia
            </Link>{" "}
            oppure{" "}
            <Link to="/pulizia-trattamento-gres" className="text-primary underline underline-offset-4">
              gres porcellanato
            </Link>
            , trattiamo anche quelli nello stesso intervento.
          </p>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <p className="eyebrow">Domande frequenti</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Quello che ci chiedono più spesso.
          </h2>
          <div className="mt-10 divide-y divide-border border-y border-border">
            {faq.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="cursor-pointer list-none text-base font-semibold marker:hidden">
                  {f.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="gradient-warm py-16 text-background">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow text-sand">Zone servite</p>
            <h2 className="mt-3 text-2xl font-semibold sm:text-3xl">
              Pulizia e lavaggio del cotto in tutta la Lombardia.
            </h2>
            <p className="mt-4 max-w-2xl text-background/85">
              Con sede a Torbole Casaglia (Brescia) interveniamo in tutte le province lombarde, dalle grandi città
              ai paesi più piccoli. Sopralluogo gratuito e preventivo senza impegno.
            </p>
            <ul className="mt-8 flex flex-wrap gap-2">
              {province.map((p) => (
                <li
                  key={p}
                  className="rounded-xs border border-background/25 px-3 py-1.5 text-sm text-background/90"
                >
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="contatti" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="eyebrow">Preventivo</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                Mandaci due foto del tuo cotto.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Bastano un paio di foto e i metri quadri indicativi per una prima stima.
                Sopralluogo e preventivo sono gratuiti e senza impegno.
              </p>
              <div className="mt-8 space-y-3">
                <a
                  href={`tel:${TEL_ANTONIO}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Antonio</span>
                  <span className="text-primary">+39 339 751 7813</span>
                </a>
                <a
                  href={`tel:${TEL_LUCA}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Luca</span>
                  <span className="text-primary">+39 351 720 4660</span>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Email</span>
                  <span className="text-primary">{EMAIL}</span>
                </a>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex rounded-xs bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-sienna"
                >
                  Scrivi ad Antonio su WhatsApp
                </a>
                <SocialLinks />
              </div>
            </div>
            <PreventivoForm />
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
